import React, {useState, useEffect, memo} from "react";
import {IoAddCircleOutline} from "react-icons/io5";
import Select from "../../components/Select";
import {createItem, deleteItem, getList} from "../../services/service";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import DeleteButton from "../../components/DeleteButton";
import FillingDataTable from "../../components/FillingDataTable";
import Pagination from "../../components/Pagination";
import {withTranslation} from "react-i18next";
import swal from "sweetalert";
import ModalAddProfileSystemSpecification from "../../components/profileSystem/ModalAddProfileSystemSpecification";
import ComboboxComponent from "../../components/Combobox";
import {Context} from "../../context/PrSpecSearchContext";
import {useContext} from "react";
import PrSpecSearchInput from "../../components/search/PrSpecSearchInput";
import {toast} from "react-toastify";
import TempDataTable from "../../components/TemDataTable";

const headerTable = [{
    name: "priority", sort: true
}, {
    name: "article", sort: true
}, {
    name: "name", sort: true
}, {
    name: "application", sort: true
}, {
    name: "sideways", sort: true
}, <span className="sr-only">Edit</span>,];

const bodyTable = ["priority", "article", "name", "applicationType", "sideType", "actions"];

const ProfileSystemSpecificationLayout = ({treeSelectId, t}) => {
    const navigate = useNavigate();
    const {token} = useSelector((state) => state.user);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [itemOffset, setItemOffset] = useState(0);
    const [forcePage, setForcePage] = useState(0);
    const [data, setData] = useState({
        data: [],
        recordsTotal: 0,
        pageCount: 1,
    });
    const [open, setOpen] = useState(false);
    const [supplierList, setSupplierList] = useState([]);
    const [supplierId, setSupplierId] = useState(null);
    const [sortColumn, setSortColumn] = useState("id");
    const [sortDir, setSortDir] = useState("DESC");
    const [clear, setClear] = useState(false);
    const ctx = useContext(Context);

    const fetchData = async (supplierId) => {
        const response = await createItem(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/profile/system/specification/table`,
            token,
            {
                draw: 1,
                start: itemOffset,
                length: itemsPerPage,
                recordsTotal: 0,
                columns: [],
                order: [{column: sortColumn, dir: sortDir}],
                search: {value: ctx.state, regex: true},
                filter: {
                    profileSystemId: treeSelectId,
                    supplierId: supplierId ? supplierId : null,
                },
            }
        );
        if (response.success) {
            setClear(false);
            let reviewData = response.data.data.map((item, index) => {
                return {
                    ...item,
                    applicationType: t(item.applicationType),
                    sideType: t(item.sideType),
                    actions: (
                        <div className="flex justify-end mt-3 sm:mt-0 sm:ml-4">
                            <DeleteButton onclick={() => deleteHandler(item.id)}/>
                        </div>
                    ),
                };
            });
            setData({
                data: reviewData,
                recordsTotal: response.data.recordsFiltered,
                pageCount: Math.ceil(
                    response.data.recordsFiltered / parseInt(itemsPerPage)
                ),
            });
        } else {
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    const fetchSupplierList = async () => {
        const response = await getList(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/supplier/list`,
            token
        );
        if (response.success) {
            setSupplierList(
                response.data.map((item) => {
                    return {
                        id: item.id,
                        name: t(`${item.name}`),
                    };
                })
            );
        } else {
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    useEffect(() => {
        if (clear) {
            fetchData();
        }
    }, [clear]);

    useEffect(() => {
        if (treeSelectId) {
            fetchData();
        }
    }, [treeSelectId, itemOffset, itemsPerPage, data.recordsTotal, ctx.state]);

    useEffect(() => {
        fetchSupplierList();
    }, []);

    const sortFunction = (value) => {
        setSortColumn(value.field ? value.field : "id");
        setSortDir(value.sort === true ? 'ASC' : 'DESC');
        setClear(true);
    }

    const handlePageClick = (event) => {
        setForcePage(event.selected);
        const newOffset =
            (event.selected * parseInt(itemsPerPage)) % data.recordsTotal;
        setItemOffset(newOffset);
    };

    const deleteHandler = async (id) => {
        const responseReceived = await swal({
            title: t(`action.delete_item.title`),
            text: t(`action.delete_item.text`),
            icon: "warning",
            buttons: {
                cancel: true,
                confirm: true,
            },
        });
        if (responseReceived) {
            const response = await deleteItem(
                `${process.env.REACT_APP_HOST_BASE_URL}/v1/profile/system/specification/${id}`,
                token
            );
            if (response.success) {
                fetchData();
                toast.success(t('action.delete_success'), {
                    position: "bottom-right",
                    autoClose: 3000,
                    closeOnClick: true,
                    className: "add-success"
                });
            } else {
                navigate("/error", {
                    state: {
                        status: response.status,
                        message: response.message ? response.message : null,
                    },
                });
            }
        } else {
            return;
        }
    };

    const handle_supplier = (value) => {
        fetchData(value.id);
        setSupplierId(value.id);
    };

    return (
        <>
            <ModalAddProfileSystemSpecification
                open={open}
                setOpen={setOpen}
                treeSelectId={treeSelectId}
                fetchData={fetchData}
            />
            <div className="mb-5 w-80">
                <h1 className="text-xl font-semibold text-gray-900">
                    {t('table.subsystem_specification')}
                </h1>
            </div>
            <div className="flex justify-between items-center">
                <ComboboxComponent
                    label={t('supplier')}
                    data={supplierList}
                    onChange={handle_supplier}
                />
                <button
                    className={`${
                        (treeSelectId && supplierId)
                            ? "flex items-center px-3 py-1 text-sm font-medium leading-4 text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            : "cursor-no-drop flex items-center px-3 py-1 text-sm font-medium leading-4 text-white bg-indigo-300 border border-transparent rounded-md shadow-sm "
                    }`}
                    onClick={() => setOpen(true)}
                    disabled={(treeSelectId && supplierId) ? false : true}
                >
                    {t('action.add')} {treeSelectId}
                    <IoAddCircleOutline className="w-4 h-4 ml-1"/>
                </button>
            </div>
            <div className="flex justify-between items-center mt-3">
                <Select
                    setItemsPerPage={setItemsPerPage}
                    itemsPerPage={itemsPerPage}
                />
                <PrSpecSearchInput/>
            </div>
            <TempDataTable
                data={data.data}
                headerTable={headerTable}
                bodyContent={bodyTable}
                sortFunction={sortFunction}
            />
            <Pagination
                handlePageClick={handlePageClick}
                pageCount={data.pageCount}
                forcePage={forcePage}
            />

        </>
    );
};

export default withTranslation()(memo(ProfileSystemSpecificationLayout));
